import { FC, useState, MouseEvent } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  confirmMessages,
  dataStorageKeywords,
  errorMessages,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { VoucherType } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { voucherTypeInitialValues } from 'src/utils/initialValues.utils';
import { voucherTypeValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import { id } from 'date-fns/locale';

const VoucherTypeActionMenuButton: FC<{
  rowId: any;
  name: string;
  status: string;
  fetchVoucherTypes: () => void;
  deleteVoucherType: (id: string, companyId: string) => void;
}> = ({ rowId, name, fetchVoucherTypes, deleteVoucherType }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedName, setName] = useState<string | null>(null);
  const [selectedVoucherType, setSelectedVoucherType] =
    useState<VoucherType | null>(null);

  const handleEditFormSubmit = async () => {
    if (!selectedVoucherType) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_VOUCHER_TYPE, {
        id: selectedVoucherType.id,
        name: formik.values.name,
        description: formik.values.description,
        displayOrder: formik.values.displayOrder,
      });
      setEditDialogOpen(false);
      fetchVoucherTypes();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (voucherType: VoucherType) => {
    setSelectedVoucherType(voucherType);

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_VOUCHER_TYPE_BY_ID,
        {
          params: { id: voucherType.id },
        }
      );

      formik.setValues({
        name: voucherType.name,
        description: response.data.description,
        displayOrder: response.data.displayOrder,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    try {
      setEditDialogOpen(false);
      setSelectedVoucherType(null);
      formik.resetForm();
    } catch (error) {
      showSnackbar(errorMessages.ERROR_CLOSING_DIALOG, 'error');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setName(name);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedName) return;
    const companyId = Cookies.get(dataStorageKeywords.COMPANY_ID);

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId && companyId) {
          await deleteVoucherType(rowId, companyId);
        }
        fetchVoucherTypes();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setName(null);
    }
  };

  const formik = useFormik({
    initialValues: voucherTypeInitialValues,
    validationSchema: voucherTypeValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const getDialogHeight = () => {
    const fieldGroups = [['name'], ['displayOrder'], ['description']];
    return calculateDialogHeight(formik, fieldGroups) + 80;
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                displayOrder: Number(null),
                name: name,
                description: '',
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title="Edit Voucher Type"
            subtitle="Update the details below to modify voucher type to the system."
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedVoucherType && (
              <>
                <TextField
                  width="480px"
                  id="name"
                  label="Voucher Type Name"
                  formikProps={formik}
                />

                <TextField
                  width="480px"
                  id="displayOrder"
                  label="Display Order"
                  formikProps={formik}
                />

                <TextArea
                  id="description"
                  label="Description"
                  formikProps={formik}
                  rows={5}
                  height="140px"
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                />
              </>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? 'Delete Voucher Type'
            : dialogType === actionButtons.ACTIVATE
            ? 'Activate Voucher Type'
            : 'Deactivate Voucher Type'
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? confirmMessages.CONFIRM_DELETE_VOUCHER_TYPE
            : dialogType === actionButtons.ACTIVATE
            ? confirmMessages.CONFIRM_ACTIVATE_PRODUCT
            : confirmMessages.CONFIRM_DEACTIVATE_PRODUCT
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : dialogType === actionButtons.ACTIVATE
            ? 'var(--warning)'
            : 'var(--error)'
        }
      />
    </>
  );
};
export default VoucherTypeActionMenuButton;
